import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Alert, Box, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { dateTime } from "src/pages/cases/format";
import { checkoutApi } from "./api";
import { money, speedStateWords } from "./words";

// Checkout options on a case (API docs/faster-decision.md,
// docs/refusal-protection.md, migration 034): the speed paid for and
// refusal protection. "Not offered: refund the speed fee" when the visa
// centre doesn't sell it, then "Refund made"; on a refusal, each protected
// traveller's refund ("Refund made") or "Not covered" with the reason, which
// the customer is emailed. Who did each, and when, stays on the case.
// Nothing shows before 034 or when the customer bought neither.

const METHOD_WORDS = { Online: "to the card they paid with, in the bank’s portal" };
const howPaid = (method) => METHOD_WORDS[method] || "the way they paid";

function Confirm({ title, children, confirmLabel, onConfirm, onClose, busy, extra = null }) {
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="xs" fullWidth aria-labelledby="checkout-confirm-title">
      <DialogTitle id="checkout-confirm-title">{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{children}</Typography>
        {extra}
      </DialogContent>
      <DialogActions>
        <ActionButton onClick={onClose} disabled={busy}>
          Not yet
        </ActionButton>
        <ActionButton variant="contained" onClick={onConfirm} busy={busy}>
          {confirmLabel}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

Confirm.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  confirmLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  extra: PropTypes.node,
};

function useAction(onChanged) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, done) => {
    setBusy(true);
    try {
      await fn();
      toast.success(done);
      setOpen(null);
      onChanged?.();
    } catch {
      // The server's message was shown; nothing changed.
    } finally {
      setBusy(false);
    }
  };
  return { open, setOpen, busy, run };
}

function Speed({ caseId, speed, canEdit, method, onChanged }) {
  const dispatch = useDispatch();
  const { open, setOpen, busy, run } = useAction(onChanged);
  return (
    <Box>
      <Typography variant="subtitle2" component="h3">
        Faster decision: {speed.name}
      </Typography>
      <Typography variant="body2">
        {money(speed.fee)} per traveller{speed.travellers > 1 ? ` × ${speed.travellers} = ${money(speed.total)}` : ""}
        {speed.official ? ` · official fee ${money(speed.official.amount, speed.official.currency)}` : ""}
        {speed.decision_time ? ` · ${speed.decision_time}` : ""}
      </Typography>
      {speed.where_offered && (
        <Typography variant="body2" color="text.secondary">
          Where offered: {speed.where_offered}
        </Typography>
      )}
      {speed.status === "refund_due" ? (
        <Alert severity="warning" icon={false} sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Refund {money(speed.total)}: the visa centre doesn’t offer {speed.name}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Marked by {speed.marked_by || "a team member"}
            {speed.marked_at ? ` · ${dateTime(speed.marked_at)}` : ""}. Refund it {howPaid(method)}, then record it here.
          </Typography>
          {canEdit && (
            <ActionButton variant="contained" size="small" sx={{ mt: 1 }} onClick={() => setOpen("refunded")}>
              Refund made
            </ActionButton>
          )}
        </Alert>
      ) : speed.status === "refunded" ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Not offered: {money(speed.total)} refunded by {speed.done_by || "a team member"}
          {speed.done_at ? ` · ${dateTime(speed.done_at)}` : ""}.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {speedStateWords(speed)}
          </Typography>
          {canEdit && speed.status === "active" && (
            <ActionButton variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setOpen("notOffered")}>
              Not offered: refund the speed fee
            </ActionButton>
          )}
        </>
      )}
      {open === "notOffered" && (
        <Confirm
          title={`The visa centre doesn’t offer ${speed.name}?`}
          confirmLabel="Yes, refund it"
          busy={busy}
          onClose={() => setOpen(null)}
          onConfirm={() => run(() => checkoutApi.speedNotOffered(caseId, dispatch), `Noted: ${money(speed.total)} to refund.`)}
        >
          The {money(speed.total)} paid for {speed.name} becomes a refund to make. Your name and the time are kept on the case, and the customer’s page says we’re refunding it.
        </Confirm>
      )}
      {open === "refunded" && (
        <Confirm
          title="Speed fee refunded?"
          confirmLabel="Yes, it’s refunded"
          busy={busy}
          onClose={() => setOpen(null)}
          onConfirm={() => run(() => checkoutApi.speedRefunded(caseId, dispatch), `Refund of ${money(speed.total)} recorded.`)}
        >
          Only once {money(speed.total)} has been refunded {howPaid(method)}. Your name and the time are kept on the case.
        </Confirm>
      )}
    </Box>
  );
}

Speed.propTypes = { caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired, speed: PropTypes.object.isRequired, canEdit: PropTypes.bool, method: PropTypes.string, onChanged: PropTypes.func };

function ClaimRow({ traveller, many, exclusions, method, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const { open, setOpen, busy, run } = useAction(onChanged);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const claim = traveller.claim;
  const who = many ? `Traveller ${traveller.traveller_number}: ` : "";
  const elsewhere = (claim.elsewhere || []).map((e) => money(e.amount, e.currency)).join(" + ");
  if (traveller.status === "refunded") {
    return (
      <Typography variant="body2" color="text.secondary">
        {who}refunded {money(claim.paid_back)}
        {elsewhere ? ` and ${elsewhere} against the receipt` : ""} by {claim.done_by || "a team member"}
        {claim.done_at ? ` · ${dateTime(claim.done_at)}` : ""}.
      </Typography>
    );
  }
  if (traveller.status === "not_covered") {
    return (
      <Typography variant="body2" color="text.secondary">
        {who}not covered ({claim.reason}), recorded by {claim.done_by || "a team member"}
        {claim.done_at ? ` · ${dateTime(claim.done_at)}` : ""}. The customer was emailed why.
      </Typography>
    );
  }
  return (
    <Alert severity="warning" icon={false}>
      <Typography variant="body2" fontWeight={700}>
        {who}Refund {money(claim.paid_back)} {howPaid(method)}
        {elsewhere ? `, and pay back ${elsewhere} against the receipt` : ""}.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Refused, with refusal protection. Within 14 days of the decision. {money(claim.paid_back)} is what they paid us for this traveller, without the protection itself.
      </Typography>
      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <ActionButton variant="contained" size="small" onClick={() => setOpen("refunded")}>
            Refund made
          </ActionButton>
          <ActionButton variant="outlined" size="small" onClick={() => setOpen("notCovered")}>
            Not covered
          </ActionButton>
        </Stack>
      )}
      {open === "refunded" && (
        <Confirm
          title="Refund made?"
          confirmLabel="Yes, it’s refunded"
          busy={busy}
          onClose={() => setOpen(null)}
          onConfirm={() => run(() => checkoutApi.protectionRefunded(traveller.id, dispatch), `Refund of ${money(claim.paid_back)} recorded.`)}
        >
          Only once {money(claim.paid_back)} has been refunded {howPaid(method)}
          {elsewhere ? ` and ${elsewhere} paid back against the receipt` : ""}. Your name and the time are kept on the case.
        </Confirm>
      )}
      {open === "notCovered" && (
        <Confirm
          title="Not covered?"
          confirmLabel="Record and email the customer"
          busy={busy}
          onClose={() => setOpen(null)}
          onConfirm={() => {
            if (!reason) {
              setReasonError(true);
              return;
            }
            run(() => checkoutApi.protectionNotCovered(traveller.id, reason, dispatch), "Recorded. The customer is emailed why.");
          }}
          extra={
            <TextField
              select
              fullWidth
              label="Why it isn’t covered"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError(false);
              }}
              error={reasonError}
              helperText={reasonError ? "Pick the reason the customer will be told." : " "}
              sx={{ mt: 2, "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px" } }}
            >
              {exclusions.map((e) => (
                <MenuItem key={e.key} value={e.key}>
                  {e.label}
                </MenuItem>
              ))}
            </TextField>
          }
        >
          The customer is emailed the reason, and can reply to have a person look at it again.
        </Confirm>
      )}
    </Alert>
  );
}

ClaimRow.propTypes = {
  traveller: PropTypes.object.isRequired,
  many: PropTypes.bool,
  exclusions: PropTypes.array.isRequired,
  method: PropTypes.string,
  canEdit: PropTypes.bool,
  onChanged: PropTypes.func,
};

export default function CheckoutCase({ caseData, canEdit, onChanged }) {
  const checkout = caseData?.checkout;
  if (!checkout || (!checkout.speed && !checkout.protection)) return null;
  const { speed, protection } = checkout;
  const claims = (protection?.travellers || []).filter((t) => t.claim);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" mb={1}>
          Checkout options
        </Typography>
        <Stack spacing={2}>
          {speed && <Speed caseId={caseData.id} speed={speed} canEdit={canEdit} method={checkout.method} onChanged={onChanged} />}
          {protection && (
            <Box>
              <Typography variant="subtitle2" component="h3">
                Refusal protection: {money(protection.price)} per traveller
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {protection.covered != null ? `Covers ${money(protection.covered)} per traveller` : "Bought"}
                {protection.rate ? ` · refusal rate ${protection.rate.percent}% (${protection.rate.year})` : ""}.
                {protection.refused ? "" : " If the visa is refused, each traveller’s refund shows here."}
              </Typography>
              {claims.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {claims.map((t) => (
                    <ClaimRow key={t.id} traveller={t} many={(protection.travellers || []).length > 1} exclusions={checkout.exclusions || []} method={checkout.method} canEdit={canEdit} onChanged={onChanged} />
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

CheckoutCase.propTypes = { caseData: PropTypes.object, canEdit: PropTypes.bool, onChanged: PropTypes.func };
